import React from 'react';
import { X, Copy, MapPin, Send, ChevronDown, Navigation, AlertTriangle, Share2, Loader2, Phone, Smartphone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/translations';
import { VerificationBadge } from './common/VerificationBadge';
import { ShareCardTemplate } from './share/ShareCardTemplate';
import { shareElementAsImage } from '../utils/shareManager';

import type { Geometry } from '../types/gis';

export interface ResultCardProps {
  title: string;
  icon?: React.ReactNode;
  themeColor: 'blue' | 'red' | 'orange' | 'indigo' | 'slate' | 'rose' | 'amber' | 'purple' | 'emerald';
  /**
   * `isWide` spans the row across both grid columns while keeping the compact pill layout,
   * for short one-line enumerations that would otherwise wrap inside a half-width cell.
   * Prefer it over `isMultiline`, which switches to the taller bordered block and drops
   * the copy button.
   */
  data: { label: string; value: string; subValue?: string; isPill?: boolean; isMultiline?: boolean; isWide?: boolean; href?: string }[];
  contactChips?: { kind: 'phone' | 'mobile' | 'email'; label: string; href: string }[];
  badges?: { label: string; color?: string; icon?: React.ReactNode }[];
  onClose: () => void;
  onDirections?: () => void;
  onReport?: (data: { label: string; value: string }[]) => void;
  actionLabel?: string;
  onAction?: (data: { label: string; value: string }[]) => void;
  onMinimize?: () => void;
  featureId?: string;
  shareUrl?: string;
  directionsUrl?: string;
  shareLabel?: string;
  mascotPath?: string;
  geometry?: Geometry | null;
  pointLocation?: [number, number] | null;
  confidence?: {
    level: 'high' | 'medium' | 'low';
    message: string;
  };
  actions?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }[];
  hideAccuracy?: boolean;
}

const colorMap = {
  blue: {
    bg: '#eff6ff',
    text: '#2563eb',
    button: '#2563eb',
    hover: '#1d4ed8'
  },
  red: {
    bg: '#fef2f2',
    text: '#ef4444',
    button: '#ef4444',
    hover: '#dc2626'
  },
  orange: {
    bg: '#fff7ed',
    text: '#f97316',
    button: '#f97316',
    hover: '#ea580c'
  },
  indigo: {
    bg: '#eef2ff',
    text: '#4f46e5',
    button: '#4f46e5',
    hover: '#4338ca'
  },
  slate: {
    bg: '#f1f5f9',
    text: '#334155',
    button: '#334155',
    hover: '#1e293b'
  },
  rose: {
    bg: '#fff1f2',
    text: '#e11d48',
    button: '#e11d48',
    hover: '#be123c'
  },
  amber: {
    bg: '#fffbeb',
    text: '#d97706',
    button: '#d97706',
    hover: '#b45309'
  },
  purple: {
    bg: '#faf5ff',
    text: '#a21caf',
    button: '#a21caf',
    hover: '#86198f'
  },
  emerald: {
    bg: '#ecfdf5',
    text: '#10b981',
    button: '#10b981',
    hover: '#059669'
  }
};

const ResultCard: React.FC<ResultCardProps> = ({
  title, icon, themeColor, data, contactChips, badges, onClose, onDirections, onReport, actionLabel, onAction, onMinimize, featureId, shareUrl, directionsUrl, shareLabel, mascotPath, geometry, pointLocation, confidence, actions, hideAccuracy
}) => {
  const colors = colorMap[themeColor];
  const { t, language } = useTranslation();
  const [isSharing, setIsSharing] = React.useState(false);
  const shareCardRef = React.useRef<HTMLDivElement>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const handleDirections = () => {
    if (onAction) {
      onAction(data);
    } else if (onDirections) {
      onDirections();
    }
  };

  const handleShare = async () => {
    if (!shareCardRef.current || !shareUrl) return;
    setIsSharing(true);
    try {
      // Dynamic localized share message based on content type
      const contentStr = shareLabel || t('LOCATION');
      const shareMsg = language === 'ta' 
        ? `நம்ம மேப்பில் இந்த ${contentStr}-ஐக் காணுங்கள்: ${title}` 
        : `Check out this ${contentStr} on NammaMap: ${title}`;

      const fullMsg = shareMsg + (directionsUrl ? `\nGoogle Maps: ${directionsUrl}` : '');

      const res = await shareElementAsImage(shareCardRef.current, {
        title: `${title} | NammaMap`,
        text: fullMsg,
        url: shareUrl,
        fileName: `nammamap-${(featureId || 'location').replace(/[^a-z0-9]/gi, '-')}.png`
      });
      
      if (res.method === 'fallback' && res.error) {
        // Silently notify success fallback via user flow or simple alert
        alert(res.error);
      }
    } catch (e) {
      console.error('Share failed', e);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <motion.div
      initial={isMobile ? { opacity: 0, y: 100 } : { opacity: 0, x: 50, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
      exit={isMobile ? { opacity: 0, y: 100 } : { opacity: 0, x: 50, scale: 0.95 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="glass result-card"
    >
      {isMobile && onMinimize && (
        <div className="result-card-drag-handle" onClick={onMinimize} />
      )}
      {/* Header */}
      <div className="result-card-header">
        <div className="result-card-title-group">
          {icon && <span style={{ color: colors.text, display: 'flex' }} aria-hidden="true">{icon}</span>}
          <h3 className="result-card-title">{title}</h3>
        </div>
        <div className="result-card-header-actions">
          {isMobile && (onDirections || onAction) && (
            <button
              onClick={handleDirections}
              className="result-card-header-btn directions-quick-btn"
              aria-label={t('GET_DIRECTIONS_FOR').replace('{{title}}', title)}
              style={{ color: colors.button }}
              title={t('DIRECTIONS')}
            >
              <Navigation size={18} aria-hidden="true" focusable="false" />
            </button>
          )}
          {onReport && (
            <button
              onClick={() => onReport(data)}
              className="result-card-header-btn"
              aria-label={t('REPORT_ISSUE')}
              title={t('REPORT_ISSUE')}
            >
              <AlertTriangle size={18} aria-hidden="true" focusable="false" />
            </button>
          )}
          {shareUrl && (
            <button
              onClick={handleShare}
              className={`result-card-header-btn ${isSharing ? 'loading' : ''}`}
              aria-label={t('SHARE_CARD_FOR').replace('{{title}}', title)}
              title="Share Card"
              disabled={isSharing}
            >
              {isSharing ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Share2 size={18} aria-hidden="true" focusable="false" />}
            </button>
          )}
          {onMinimize && isMobile && (
            <button
              onClick={onMinimize}
              className="result-card-header-btn"
              aria-label={t('MINIMIZE_CARD')}
              style={{ color: '#f59e0b' }}
            >
              <ChevronDown size={18} aria-hidden="true" focusable="false" />
            </button>
          )}
          <button
            onClick={onClose}
            className="result-card-header-btn close"
            aria-label={t('CLOSE')}
          >
            <X size={18} aria-hidden="true" focusable="false" />
          </button>
        </div>
      </div>

      {/* Badges Section */}
      {badges && badges.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '0 16px 12px 16px' }}>
          {badges.map((badge, i) => (
            <div 
              key={i} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                padding: '3px 8px', 
                borderRadius: '6px', 
                fontSize: '10px', 
                fontWeight: 700,
                background: badge.color ? `${badge.color}15` : colors.bg,
                color: badge.color || colors.text,
                border: `1px solid ${badge.color ? `${badge.color}30` : 'transparent'}`
              }}
            >
              <span aria-hidden="true">{badge.icon}</span>
              <span>{badge.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Optional Mascot Section */}
      {mascotPath && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 16px 16px 16px', overflow: 'visible' }}>
          <img 
            src={mascotPath} 
            alt="N-Bot" 
            className="hero-mascot"
            style={{ 
              width: isMobile ? '100px' : '120px', 
              height: 'auto', 
              marginBottom: 0,
              filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.15))'
            }}
          />
        </div>
      )}

      {/* Accuracy Disclaimer */}
      {!hideAccuracy && (!isMobile || !badges?.some(b => b.label === t('INDICATIVE_DATA_DISCLAIMER'))) && (
        <div style={{ 
          padding: isMobile ? '6px 10px' : '8px 12px', 
          margin: isMobile ? '0 16px 8px 16px' : '0 16px 12px 16px', 
          borderRadius: '8px', 
          background: 'rgba(0,0,0,0.03)', 
          border: '1px dashed rgba(0,0,0,0.1)',
          fontSize: '10px',
          color: 'var(--text-secondary)',
          lineHeight: '1.4',
          display: 'flex',
          gap: '6px'
        }}>
          <span style={{ fontSize: '12px', marginTop: '-1px' }}>📍</span>
          <span><strong>{t('ACCURACY')}:</strong> {t('VICINITY_DISCLAIMER')}</span>
        </div>
      )}

      {/* Match Confidence */}
      {confidence && (
        <div style={{ 
          padding: '8px 12px', 
          margin: '0 16px 12px 16px', 
          borderRadius: '8px', 
          background: confidence.level === 'high' ? 'rgba(34,197,94,0.05)' : confidence.level === 'medium' ? 'rgba(234,179,8,0.05)' : 'rgba(239,68,68,0.05)', 
          border: `1px solid ${confidence.level === 'high' ? 'rgba(34,197,94,0.15)' : confidence.level === 'medium' ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)'}`,
          fontSize: '10px',
          color: 'var(--text-secondary)',
          lineHeight: '1.4',
          display: 'flex',
          gap: '6px'
        }}>
          <span style={{ fontSize: '12px', marginTop: '-1px' }}>
            {confidence.level === 'high' ? '✅' : confidence.level === 'medium' ? '⚠️' : '🔍'}
          </span>
          <span><strong>{t('MATCH_CONFIDENCE')}:</strong> {confidence.message}</span>
        </div>
      )}

      {/* Community Verification */}
      {featureId && (
        <VerificationBadge 
          featureId={featureId} 
          themeColor={themeColor} 
          onReport={onReport}
          data={data}
        />
      )}

      {/* Body */}
      <div className="result-card-body">
        <div className="result-card-grid-container">
          {data.map((row, idx) => {
            const valueStr = typeof row.value === 'string' ? row.value : String(row.value ?? '');
            const isRowMultiline = row.isMultiline || valueStr.length > 45 || valueStr.includes('\n');
            return (
              <div key={idx} className={`result-card-row ${row.subValue ? 'has-subvalue' : ''} ${isRowMultiline ? 'multiline full-width' : row.isWide ? 'grid-item full-width' : 'grid-item'}`}>
                {isRowMultiline ? (
                  <div className="result-card-multiline-row">
                    <span className="result-card-label">{row.label}</span>
                    <div className="result-card-value-group">
                      <span 
                        className="result-card-value multiline-value" 
                        style={{ 
                          whiteSpace: 'pre-line',
                          backgroundColor: colors.bg,
                          borderLeft: `3px solid ${colors.button}`,
                          padding: '8px 12px',
                          borderRadius: '8px',
                          width: '100%',
                          textAlign: 'left',
                          lineHeight: '1.4',
                          fontSize: '12px',
                          color: 'var(--text-primary)',
                          fontWeight: 500
                        }}
                        title={row.value}
                      >
                        {row.value}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="result-card-main-row">
                    <span className="result-card-label">{row.label}</span>
                    <div className="result-card-value-group">
                      {row.isPill ? (
                        <span
                          className="result-card-pill"
                          style={{ backgroundColor: colors.bg, color: colors.text }}
                        >
                          {row.value}
                        </span>
                      ) : (
                        row.href ? (
                          <a 
                            href={row.href} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="result-card-value" 
                            style={{ color: colors.text, textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            {row.value}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          </a>
                        ) : (
                        <span className="result-card-value" title={row.value}>{row.value}</span>
                        )
                      )}
                      {row.isPill && (
                        <button
                          onClick={() => handleCopy(row.value)}
                          className="copy-button"
                          aria-label={t('COPY_LABEL').replace('{{label}}', row.label)}
                        >
                          <Copy size={14} color="var(--text-secondary)" aria-hidden="true" focusable="false" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {row.subValue && (
                  <span className="result-card-subvalue">{row.subValue}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Compact contact strip (e.g. police SHO call / mobile / email) */}
      {contactChips && contactChips.length > 0 && (
        <div className="result-card-contact-bar">
          {contactChips.map((chip, i) => (
            <a
              key={i}
              href={chip.href}
              className="result-card-contact-chip"
              style={{ backgroundColor: colors.bg, color: colors.text }}
              aria-label={`${chip.kind}: ${chip.label}`}
            >
              {chip.kind === 'phone' && <Phone size={14} aria-hidden="true" focusable="false" />}
              {chip.kind === 'mobile' && <Smartphone size={14} aria-hidden="true" focusable="false" />}
              {chip.kind === 'email' && <Mail size={14} aria-hidden="true" focusable="false" />}
              <span>{chip.label}</span>
            </a>
          ))}
        </div>
      )}

      {(onDirections || onAction) && !actions && (
        <div className="result-card-footer">
          <button
            onClick={handleDirections}
            className="result-card-action-button"
            style={{ backgroundColor: colors.button }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.button)}
            aria-label={actionLabel ? `${actionLabel} - ${title}` : t('GET_DIRECTIONS_FOR').replace('{{title}}', title)}
          >
            {onAction ? <Send size={16} aria-hidden="true" focusable="false" /> : <MapPin size={16} aria-hidden="true" focusable="false" />}
            {onAction ? actionLabel || t('SEND') : actionLabel || t('DIRECTIONS')}
          </button>
        </div>
      )}
      {actions && actions.length > 0 && (
        <div 
          className="result-card-footer" 
          style={{ 
            display: 'flex', 
            flexDirection: actions.length > 1 ? 'row' : 'column', 
            gap: '8px', 
            padding: '12px 16px'
          }}
        >
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={action.onClick}
              className="result-card-action-button"
              style={{ 
                backgroundColor: action.variant === 'secondary' ? 'rgba(0,0,0,0.05)' : colors.button,
                color: action.variant === 'secondary' ? 'var(--text-primary)' : '#fff',
                width: actions.length > 1 ? 'auto' : '100%',
                flex: actions.length > 1 ? 1 : undefined,
                justifyContent: 'center',
                borderRadius: '12px',
                padding: actions.length > 1 ? '12px 8px' : '16px',
                fontSize: actions.length > 1 ? '12px' : '13px'
              }}
              aria-label={action.label}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      )}
      {/* Off-screen Share Card Renderer */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', zIndex: -100 }}>
        <ShareCardTemplate 
          innerRef={shareCardRef}
          title={title}
          icon={icon}
          themeColor={themeColor}
          data={data}
          geometry={geometry}
          pointLocation={pointLocation}
          shareLabel={shareLabel}
        />
      </div>
    </motion.div>
  );
};

export default ResultCard;
